import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { useRequireAuth } from '@/hooks/use-require-auth';
import { listNotifications, markRead, markAllRead } from '@/lib/notifications-api';
import { approveMember, denyMember, restorePending } from '@/lib/events-api';
import {
  ArrowLeft, Bell, UserPlus, UploadCloud, DownloadCloud,
  CheckCircle, XCircle, PauseCircle, MinusCircle, Send, BookOpen,
  AlertCircle, ChevronRight, Check, X, Loader2,
} from 'lucide-react';

const ICON_FOR = {
  HOST_APPROVAL_REQUESTED: UserPlus, NEW_UPLOADS_DIGEST: UploadCloud,
  FRAME_EXPORT_READY: DownloadCloud, MEMBERSHIP_APPROVED: CheckCircle,
  MEMBERSHIP_DENIED: XCircle, MEMBERSHIP_SUSPENDED: PauseCircle,
  MEMBERSHIP_REMOVED: MinusCircle, UPLOAD_DELIVERED_DIGEST: Send, LOOKBOOK_LIVE: BookOpen,
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Math.max(0, Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { ready } = useRequireAuth();
  const [rows, setRows] = useState([]);
  const [unread, setUnread] = useState(0);
  const [pinnedCount, setPinnedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rowBusy, setRowBusy] = useState({});
  const [rowError, setRowError] = useState({});
  const [flash, setFlash] = useState(null);
  const flashTimer = useRef(null);
  const undoUsedRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listNotifications(false, 50);
      setRows(r.notifications || []);
      setUnread(r.unread_count || 0);
      setPinnedCount(r.pinned_count || 0);
      setError(null);
    } catch (e) {
      setError(e?.message || 'Failed to load notifications.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (ready) load(); }, [ready, load]);
  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  function showFlash(f, ms = 2600) {
    undoUsedRef.current = false;
    setFlash(f);
    if (flashTimer.current) clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), f.undo ? 6000 : ms);
  }

  async function onUndo(flashData) {
    if (undoUsedRef.current || !flashData?.undo) return;
    undoUsedRef.current = true;
    const { eventId, memberId, rowId } = flashData.undo;
    try {
      await restorePending(eventId, memberId);
      // Re-pin locally
      setRows(prev => prev.map(r => r.id === rowId ? { ...r, pinned: true } : r));
      setPinnedCount(c => c + 1);
      setFlash(null);
    } catch (e) {
      showFlash({ tone: 'danger', text: e?.message || 'Undo failed.' });
    }
  }

  const { pinnedRows, feedRows } = useMemo(() => {
    const p = rows.filter(r => r.pinned);
    const f = rows.filter(r => !r.pinned);
    return { pinnedRows: p, feedRows: f };
  }, [rows]);

  function unpinLocal(rowId) {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, pinned: false } : r));
    setPinnedCount(c => Math.max(0, c - 1));
  }

  async function onApprove(row) {
    if (!row.event_id) return;
    const memId = row.extra?.membership_id;
    if (!memId || rowBusy[row.id]) return;
    setRowBusy(b => ({ ...b, [row.id]: 'approve' }));
    setRowError(e => ({ ...e, [row.id]: undefined }));
    const name = row.actor_first_name || 'the guest';
    try {
      await approveMember(row.event_id, memId);
      unpinLocal(row.id);
      showFlash({ tone: 'success', text: `Approved ${name}. Uploads unlocked.`, undo: { eventId: row.event_id, memberId: memId, rowId: row.id } });
    } catch (e) {
      setRowError(prev => ({ ...prev, [row.id]: e?.message || 'Approve failed. Try again.' }));
      showFlash({ tone: 'danger', text: e?.message || 'Approve failed. Try again.' });
    } finally {
      setRowBusy(b => { const { [row.id]: _, ...rest } = b; return rest; });
    }
  }

  async function onDeny(row) {
    if (!row.event_id) return;
    const memId = row.extra?.membership_id;
    if (!memId || rowBusy[row.id]) return;
    const name = row.actor_first_name || 'the guest';
    const ok = window.confirm(
      `Deny ${name}\u2019s request? They will remain locked out of uploads. You can approve them later from the guests screen.`
    );
    if (!ok) return;
    setRowBusy(b => ({ ...b, [row.id]: 'deny' }));
    setRowError(e => ({ ...e, [row.id]: undefined }));
    try {
      await denyMember(row.event_id, memId, 'Denied from notifications feed');
      unpinLocal(row.id);
      showFlash({ tone: 'success', text: `Denied ${name}\u2019s request.`, undo: { eventId: row.event_id, memberId: memId, rowId: row.id } });
    } catch (e) {
      setRowError(prev => ({ ...prev, [row.id]: e?.message || 'Deny failed. Try again.' }));
      showFlash({ tone: 'danger', text: e?.message || 'Deny failed. Try again.' });
    } finally {
      setRowBusy(b => { const { [row.id]: _, ...rest } = b; return rest; });
    }
  }

  async function onTap(row) {
    if (!row.read_at) {
      try {
        await markRead(row.id);
        setRows(prev => prev.map(r => r.id === row.id ? { ...r, read_at: new Date().toISOString() } : r));
        setUnread(u => Math.max(0, u - 1));
      } catch { /* silent */ }
    }
    if (row.kind === 'FRAME_EXPORT_READY' && row.extra?.job_id && row.event_id) {
      navigate(`/frame-collection?event_id=${encodeURIComponent(row.event_id)}&collection_id=${encodeURIComponent(row.extra.collection_id || '')}`);
    } else if (row.kind === 'HOST_APPROVAL_REQUESTED' && row.event_id) {
      navigate(`/host-guests?event_id=${encodeURIComponent(row.event_id)}`);
    } else if (row.kind === 'LOOKBOOK_LIVE') {
      navigate('/contrib-event');
    } else if (row.kind === 'MEMBERSHIP_APPROVED' || row.kind === 'MEMBERSHIP_DENIED') {
      navigate('/contrib-event');
    } else if (row.kind === 'NEW_UPLOADS_DIGEST' && row.event_id) {
      navigate(`/host-vault?event_id=${encodeURIComponent(row.event_id)}`);
    } else if (row.kind === 'UPLOAD_DELIVERED_DIGEST' && row.event_id) {
      navigate(`/my-views?event_id=${encodeURIComponent(row.event_id)}`);
    }
  }

  async function onMarkAll() {
    setBusy(true);
    try {
      await markAllRead();
      setRows(prev => prev.map(r => ({ ...r, read_at: r.read_at || new Date().toISOString() })));
      setUnread(0);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 relative" data-testid="notifications-screen">
      {/* ---- Flash toast ---- */}
      {flash && (
        <div
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-2.5 rounded-xl border shadow-lg max-w-md ${
            flash.tone === 'success'
              ? 'bg-[#E4F4EC] border-[#1F8A55]'
              : 'bg-[#FBE5E5] border-[#C93434]'
          }`}
          data-testid={`flash-${flash.tone}`}
        >
          {flash.tone === 'success'
            ? <CheckCircle className="w-4 h-4 text-[#1F8A55] flex-shrink-0" />
            : <AlertCircle className="w-4 h-4 text-[#C93434] flex-shrink-0" />}
          <span className={`text-[13px] font-bold ${flash.tone === 'success' ? 'text-[#1F8A55]' : 'text-[#C93434]'}`}>
            {flash.text}
          </span>
          {flash.undo && !undoUsedRef.current && (
            <button
              onClick={() => onUndo(flash)}
              className="ml-2 px-2 py-0.5 text-[12px] font-extrabold text-[#146BFF] bg-white border border-[#146BFF] rounded hover:bg-[#EFF5FF] transition-colors"
              data-testid="flash-undo-btn"
            >
              Undo
            </button>
          )}
        </div>
      )}

      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-[#146BFF] font-semibold text-sm mb-4 hover:underline" data-testid="notifications-back">
        <ArrowLeft className="w-4 h-4" /> back
      </button>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-extrabold text-[#0B1B46]">Notifications</h1>
        {unread > 0 && (
          <button onClick={onMarkAll} disabled={busy} className="text-[#146BFF] text-sm font-bold hover:underline disabled:opacity-50" data-testid="notifications-mark-all-btn">
            Mark all read
          </button>
        )}
      </div>

      {loading && !rows.length ? (
        <div className="flex justify-center py-8" data-testid="notifications-loading">
          <div className="w-8 h-8 border-2 border-[#146BFF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : error ? (
        <div className="bg-[#FBE5E5] border border-[#C93434]/20 rounded-lg p-3 text-sm text-[#C93434]" data-testid="notifications-error">{error}</div>
      ) : rows.length === 0 ? (
        <div className="bg-white border border-[#E3E8EF] rounded-xl p-6 text-center" data-testid="notifications-empty">
          <p className="text-sm text-[#5B6472]">Nothing here yet. When someone acts on your events, you&apos;ll see it here.</p>
        </div>
      ) : (
        <>
          {/* ---- Phase 9c.1: Pinned section with inline Approve / Deny ---- */}
          {pinnedRows.length > 0 && (
            <div className="bg-[#FFF6E5] border border-[#B86500] rounded-xl p-4 mb-4 flex flex-col gap-3" data-testid="pinned-section">
              <div>
                <div className="flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 text-[#B86500]" />
                  <span className="text-[15px] font-extrabold text-[#B86500] uppercase tracking-wide" data-testid="pinned-section-title">
                    Needs your approval
                  </span>
                  <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#B86500] text-white text-xs font-extrabold flex items-center justify-center" data-testid="pinned-section-count">
                    {pinnedCount || pinnedRows.length}
                  </span>
                </div>
                <p className="text-xs text-[#0B1B46] mt-1 leading-[16px]">
                  {pinnedRows.length === 1
                    ? 'One guest is waiting for you to approve or deny their upload access.'
                    : `${pinnedRows.length} guests are waiting for you to approve or deny their upload access.`}
                </p>
              </div>

              {pinnedRows.map(r => (
                <PinnedRow
                  key={r.id}
                  row={r}
                  busy={rowBusy[r.id]}
                  error={rowError[r.id]}
                  onApprove={() => onApprove(r)}
                  onDeny={() => onDeny(r)}
                  onReview={() => onTap(r)}
                />
              ))}
            </div>
          )}

          {/* ---- Chronological feed ---- */}
          {feedRows.length > 0 && (
            <div data-testid="feed-section">
              {pinnedRows.length > 0 && (
                <p className="text-xs text-[#5B6472] uppercase tracking-wide mb-2 mt-1">Everything else</p>
              )}
              <div className="flex flex-col gap-2">
                {feedRows.map(r => {
                  const Icon = ICON_FOR[r.kind] || Bell;
                  return (
                    <button key={r.id} onClick={() => onTap(r)}
                      className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${!r.read_at ? 'bg-[#F1F7FF] border-[#146BFF]' : 'bg-white border-[#E3E8EF] hover:bg-[#F3F5F8]'}`}
                      data-testid={`notification-row-${r.id}`}>
                      <div className="w-8 h-8 rounded-full bg-[#F3F5F8] flex items-center justify-center flex-shrink-0">
                        <Icon className={`w-4 h-4 ${!r.read_at ? 'text-[#146BFF]' : 'text-[#5B6472]'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="text-sm font-bold text-[#0B1B46] truncate" data-testid={`notification-summary-${r.id}`}>{r.summary}</span>
                          {r.count > 1 && <span className="px-1.5 py-0.5 text-[10px] font-extrabold bg-[#146BFF] text-white rounded">&times;{r.count}</span>}
                        </div>
                        <p className="text-xs text-[#5B6472] mt-0.5">{r.event_name ? `${r.event_name} · ` : ''}{timeAgo(r.updated_at || r.created_at)}</p>
                      </div>
                      {!r.read_at && <div className="w-2 h-2 rounded-full bg-[#146BFF] flex-shrink-0" data-testid={`notification-unread-dot-${r.id}`} />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      <p className="text-center text-[11px] text-[#5B6472] mt-6" data-testid="notifications-footer">
        Signed in as {user?.email}. Notifications never carry your media — just a status line.
      </p>
    </div>
  );
}

function PinnedRow({ row, busy, error: rowErr, onApprove, onDeny, onReview }) {
  const approving = busy === 'approve';
  const denying = busy === 'deny';
  const anyBusy = !!busy;

  return (
    <div className="p-3 rounded-lg bg-white border border-[#B86500] flex flex-col gap-2.5" data-testid={`pinned-row-${row.id}`}>
      <div className="flex items-center gap-3">
        <div className="w-[34px] h-[34px] rounded-full bg-[#FFF6E5] border border-[#B86500] flex items-center justify-center flex-shrink-0">
          <UserPlus className="w-[18px] h-[18px] text-[#B86500]" />
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-sm font-bold text-[#0B1B46] line-clamp-2" data-testid={`pinned-summary-${row.id}`}>{row.summary}</span>
          <p className="text-xs text-[#5B6472] mt-0.5">{row.event_name ? `${row.event_name} · ` : ''}{timeAgo(row.updated_at || row.created_at)}</p>
          {rowErr && <p className="text-xs text-[#C93434] mt-1 font-semibold" data-testid={`pinned-row-error-${row.id}`}>{rowErr}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={onApprove}
          disabled={anyBusy}
          className={`inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-[13px] font-bold min-w-[96px] transition-colors ${
            anyBusy ? 'bg-[#146BFF]/60 text-white cursor-not-allowed' : 'bg-[#146BFF] hover:bg-[#0F55CC] text-white'
          }`}
          data-testid={`pinned-approve-btn-${row.id}`}
        >
          {approving
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <><Check className="w-3.5 h-3.5" /> Approve</>}
        </button>
        <button
          onClick={onDeny}
          disabled={anyBusy}
          className={`inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg text-[13px] font-bold min-w-[84px] border transition-colors ${
            anyBusy ? 'border-[#C93434]/40 text-[#C93434]/40 cursor-not-allowed' : 'border-[#C93434] text-[#C93434] hover:bg-[#FBE5E5]'
          }`}
          data-testid={`pinned-deny-btn-${row.id}`}
        >
          {denying
            ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#C93434]" />
            : <><X className="w-3.5 h-3.5" /> Deny</>}
        </button>
        <button
          onClick={onReview}
          disabled={anyBusy}
          className="inline-flex items-center gap-0.5 px-2.5 py-2 rounded-lg text-[13px] font-bold text-[#146BFF] bg-[#F3F5F8] border border-[#E3E8EF] hover:bg-[#EFF5FF] transition-colors disabled:opacity-50"
          data-testid={`pinned-review-btn-${row.id}`}
        >
          Review <ChevronRight className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
